<template>
  <div></div>
</template>

<style lang="scss" scoped></style>

<script>
import Shepherd from 'shepherd.js';

import { showFlash } from '@/alerts';
import EventBus from '@/event-bus';
import * as log from '@/logger';
import { useToastStore } from '@/stores/toast';
import { routeTo } from '@/utils/cluster-utils';
import { sleep } from '@/utils/fetch-utils';
import { get, set } from '@/utils/settings';

const TOUR_BUTTON_LABELS = {
  EXIT: 'Exit',
  NEXT: 'Next',
  BACK: 'Back',
  FINISH: 'Finish',
};

export default {
  name: 'GuidedTour',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tour: null,
      cookieName: 'tourDismissed',
      welcomeDialogTimeout: null,
    };
  },
  watch: {
    $route(to) {
      if (to.name === 'Logout') {
        this.tour.cancel();
      }
    },
  },
  methods: {
    /**
     * User has declined the tour.
     * We save this in localStorage so they won't be asked again.
     */
    onDismiss() {
      log.info('Tour dismissed/completed. Saving setting...');
      this.showDismissDialog();
    },
    startTour() {
      useToastStore().hideToasts();
      routeTo({ name: 'Dashboard' });
      this.tour.start();
    },
    async ensureOptionSelected(optionSelector) {
      try {
        const option = document.querySelector(optionSelector);
        if (!option) {
          return;
        }

        const isSelected = option.getAttribute('aria-pressed') === 'true';
        if (isSelected) {
          return;
        }

        await option.click();
        await this.$nextTick();
      } catch (err) {
        log.error('Failed to ensure option is selected in guided tour', {
          selector: optionSelector,
          error: err,
        });
      }
    },
    showTourWelcomeDialog() {
      // Mark the tour dismissed right away.  User will either dismiss it or start it.
      set(this.cookieName, true);

      showFlash({
        message: 'Welcome to the Kasten Dashboard',
        details: 'First time using Kasten? We can we show you around.',
        buttons: [
          {
            text: 'Take a Quick Tour',
            icon: 'icon-tour',
            clickFn: this.startTour,
          },
          {
            text: 'No, Thanks',
            clickFn: this.onDismiss,
          },
        ],
        messageType: 'info',
        icon: 'icon-light-on',
        dismiss: 'user',
      });
    },
    showDismissDialog() {
      showFlash({
        message: 'Restart the guided tour anytime from the User Menu in the top right of the app.',
        messageType: 'info',
        dismiss: 5000,
        icon: 'icon-tour',
      });
    },
    configureTour() {
      const menuButton = document.querySelector('#mobileMenuButton');
      let isMobile = false;
      try {
        isMobile = menuButton.checkVisibility();
      } catch (error) {
        // checkVisibility doesn't work in Safari https://caniuse.com/mdn-api_element_checkvisibility
        isMobile = window.innerWidth < 992;
      }
      const IDType = isMobile === true ? 'Mobile' : '';

      this.tour = new Shepherd.Tour({
        useModalOverlay: true,
        keyboardNavigation: true,
        defaultStepOptions: {
          classes: '',
          scrollTo: { behavior: 'smooth', block: 'center' },
          canClickTarget: false,
          cancelIcon: { enabled: true, label: 'Exit the Kasten tour' },
          modalOverlayOpeningPadding: 10,
        },
      });

      this.tour.on('cancel', () => {
        if (this.$route.name !== 'Logout') {
          this.onDismiss();
          useToastStore().showToasts();
        }
      });

      this.tour.on('complete', () => {
        log.info('Guided tour completed.');
        routeTo({ name: 'Dashboard' });
        this.onDismiss();
        useToastStore().showToasts();
      });

      const completeTourButton = {
        text: TOUR_BUTTON_LABELS.FINISH,
        classes: 'button-complete-tour',
        action: this.tour.complete,
      };

      const exitTourButton = {
        text: TOUR_BUTTON_LABELS.EXIT,
        action: this.tour.cancel,
        classes: 'button-cancel',
      };

      const backButton = {
        text: TOUR_BUTTON_LABELS.BACK,
        classes: 'button-back',
        action: this.tour.back,
      };

      const nextButton = {
        text: TOUR_BUTTON_LABELS.NEXT,
        classes: 'button-next',
        action: this.tour.next,
      };

      const vmButton = {
        text: TOUR_BUTTON_LABELS.NEXT,
        classes: 'button-next',
        action: async () => {
          const vmToggle = document.querySelector(
            '#policy-by-namespace-or-vms [data-testid="policy-form-policy-by-namespace-or-vms-btn-group-radio-option-Virtual Machines"]',
          );
          if (vmToggle) {
            vmToggle.click();
          }
          this.tour.next();
        },
      };
      const nextButtonSelectHourly = {
        text: TOUR_BUTTON_LABELS.NEXT,
        classes: 'button-next',
        action: async () => {
          const hourlyOption = document.querySelector('#k-button-group-option-hourly');
          if (hourlyOption) {
            await hourlyOption.click();
          }
          this.tour.next();
        },
      };

      const nextButtonSelectByName = {
        text: TOUR_BUTTON_LABELS.NEXT,
        classes: 'button-next',
        action: async () => {
          const byNameOption = document.querySelector('#k-button-group-option-by-name');
          if (byNameOption) {
            await byNameOption.click();
          }
          this.tour.next();
        },
      };

      const defaultBackAndNextButtons = [backButton, nextButton];
      const selectHourlyFrequencyAndProceedButtons = [backButton, nextButtonSelectHourly];
      const selectByNameAndProceedButtons = [backButton, nextButtonSelectByName];

      this.tour.addStep({
        id: 'step-dashboard-page',
        title: 'Kasten Guided Tour',
        text: `Okay we'll start here on the Dashboard page where you'll see summary information about the state of your Kasten system.`,
        buttons: [exitTourButton, nextButton],
      });

      this.tour.addStep({
        id: 'step-apps-card',
        text: `<p>Applications (namespaces) that Kasten has discovered in your cluster are summarized on this card.</p>
      <p>Clicking this card takes you to the Applications page. We'll take a look at that page later.</p>`,
        attachTo: {
          element: '[data-testid=compliance-apps-card]',
          on: isMobile ? 'bottom' : 'right',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policies-card',
        text: `<p>This card summarizes the policies you have created for managing applications.</p>
      <p>Clicking this card will take you to the Policies page where you can view, edit, and create policies.</p>`,
        attachTo: {
          element: '[data-testid=compliance-policies-card]',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-data-card',
        text: `This is a summary of the data being used by Kasten, both inside and outside the
      cluster. On the Data page you will find charts for reviewing historical data usage.`,
        attachTo: {
          element: '[data-testid=compliance-usage-and-reports-card]',
          on: isMobile ? 'top' : 'left',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-activity-chart',
        text: `As policies are executed, jobs will appear here and in the list below. You'll be able to see
      job phases complete in real-time.  Click any job to see details such as job artifacts, errors, and related kubectl commands.`,
        attachTo: {
          element: 'section.activity .activity-chart',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour
        .addStep({
          id: 'step-activity-stats',
          text: `Summary stats will provide a high-level view of historical activity over time.`,
          attachTo: {
            element: 'section.activity .summary-metrics',
            on: 'bottom',
          },
          buttons: defaultBackAndNextButtons,
        })
        .on('before-show', () => {
          // if navigating back close sidebar if it is open
          const closeBtn = document.querySelector('#mobileMenuCloseButton');
          if (closeBtn?.checkVisibility()) closeBtn.click();
        });

      const settingsBtnID = `#sidebarButton${IDType}Settings`;
      this.tour.addStep({
        id: 'step-settings-button',
        text: `In Settings you can enable Kasten DR (for Kasten self-protection), view license status and system information as well as adjust Kasten UI settings.`,
        attachTo: {
          element: settingsBtnID,
          on: isMobile ? 'top' : 'right',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          const closeBtn = document.querySelector('#mobileMenuCloseButton');
          if (isMobile && !closeBtn?.checkVisibility()) {
            menuButton.click();
            await sleep(500);
            await this.$nextTick();
          }
          const settingsBtn = document.querySelector(settingsBtnID);
          if (settingsBtn.ariaExpanded === 'false') {
            settingsBtn.click();
          }
        },
      });

      const appsLinkID = `#sidebarLink${IDType}Applications`;
      this.tour.addStep({
        id: 'step-pre-apps-page',
        text: `Next we'll take a closer look at the Applications page.`,
        arrow: true,
        attachTo: {
          element: appsLinkID,
          on: 'right',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          if (this.$route.name !== 'Dashboard') await routeTo({ name: 'Dashboard' });
          const closeBtn = document.querySelector('#mobileMenuCloseButton');
          if (isMobile && !closeBtn?.checkVisibility()) {
            menuButton.click();
            await sleep(500);
            await this.$nextTick();
          }
        },
      });

      // if we are navigating from the Dashboard, route to the Apps page if we aren't on it already
      this.tour
        .addStep({
          id: 'step-apps-page',
          title: 'Applications Page',
          text: `This is the Applications page where you can review the
      compliance status and the details of your cluster's applications.`,
          buttons: defaultBackAndNextButtons,
        })
        .on('before-show', () => {
          if (this.$route.name !== 'Apps') routeTo({ name: 'Apps' });
        });

      this.tour
        .addStep({
          id: 'step-namespace-row',
          text: `<p>On this application row you'll see summary statistics and compliance status for the application.</p>
    <p>On the right of the application row is the action menu with action items such as
    snapshot (one-time), restore, export, create a policy and application details.</p>
    <p>For unmanaged applications, you'll see a convenient <i>Create a Policy</i> button that, when clicked, will open and
    pre-populate the new policy form for you.</p>`,
          attachTo: {
            element: '.guidedTour-namespaceRow',
            on: 'bottom',
          },
          buttons: defaultBackAndNextButtons,
        })
        .on('before-show', () => {
          if (this.$route.name !== 'Apps') routeTo({ name: 'Apps' });
          // if navigating back close sidebar if it is open
          const closeBtn = document.querySelector('#mobileMenuCloseButton');
          if (closeBtn?.checkVisibility()) closeBtn.click();
        });

      const policiesBtnID = `#sidebarButton${IDType}Policies`;
      this.tour.addStep({
        id: 'step-dashboard-pre-policies-nav-options-card',
        text: `Now let's check out the Policies Page.`,
        attachTo: {
          element: policiesBtnID,
          on: isMobile ? 'top' : 'right',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          if (this.$route.name !== 'Apps') await routeTo({ name: 'Apps' });
          if (isMobile) {
            menuButton.click();
            await sleep(500);
            await this.$nextTick();
          }
          const policiesBtn = document.querySelector(policiesBtnID);
          if (policiesBtn.ariaExpanded === 'false') {
            policiesBtn.click();
          }
        },
      });

      this.tour
        .addStep({
          id: 'step-policies-page',
          title: 'Policies Page',
          text: `This is the Policies page where you can view, create, and edit policies for managing data in your cluster.`,
          buttons: defaultBackAndNextButtons,
        })
        .on('before-show', () => {
          if (this.$route.name !== 'Policies') routeTo({ name: 'Policies' });
        });

      this.tour.addStep({
        id: 'step-new-policy',
        text: `<p>Let's walk through the process of creating a simple backup policy.</p>
      <p>Don't worry, we won't submit the form.</p>`,
        attachTo: { element: '#create-policy-btn', on: 'bottom' },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-name',
        text: `<p>First we choose a name for the policy.</p>
        <p>It must use DNS-style syntax - lowercase, dashes and dots are ok.</p>`,
        attachTo: {
          element: '#policy-form-group-name',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          const selector = { type: 'namespace', namespace: 'gitlab' };
          EventBus.$emit('policy-form-prefill', selector);
          await sleep(500);
          await this.$nextTick();
        },
      });

      this.tour.addStep({
        id: 'step-policy-form-action',
        text: `The primary action can be either Snapshot or Import. Here we've chosen <i>Snapshot</i>.`,
        attachTo: {
          element: '#policy-form-group-action',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-freq',
        text: `This controls the frequency at which this policy runs.
For this example, we will select 'Hourly'; our policy will now run on the hour, every hour.`,
        attachTo: {
          element: '#policy-form-group-frequency',
          on: 'bottom',
        },
        buttons: selectHourlyFrequencyAndProceedButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-freq-subhourly',
        text: `<p>If you choose an hourly frequency, you'll have the additional option to perform
        the action multiple times per hour, as frequently as 5-minute intervals.</p>`,
        attachTo: {
          element: '[data-testid=show-advanced-freq-options]',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          await this.ensureOptionSelected('#k-button-group-option-hourly');
        },
      });

      this.tour.addStep({
        id: 'step-policy-form-retention',
        text: `Choose how many restore points to retain.`,
        attachTo: {
          element: '#policy-snapshot-retention',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-export',
        text: `<p>Check this box if you'd like to configure the policy to export some or all of the
        restore points to external storage.</p>
        <p>You need to select this option to enable
        true backups with some cloud and on-prem storage providers.</p>
        <p>You'll need to create an export profile in Settings for this.</p>`,
        attachTo: {
          element: '#policy-export-after-snapshot',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-ns-or-vm-select',
        text: `<p>Choose to create a policy targeting whole namespaces (applications) or Virtual Machines within a namespace.</p>`,
        attachTo: {
          element: '#policy-form-by-namespace-or-vms',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-policy-form-ns-select',
        text: `<p>When selecting by Namespaces, choose which applications this policy should apply to.  For the sake of example,
        we've selected an application called <i>gitlab</i>. You can choose one or more applications to target.</p>
        <p>Or you can select applications by <i>label</i> by choosing one or more labels from a list.
        Any applications that match the labels, now or in the future, will be targeted by this policy.</p>`,
        attachTo: {
          element: '#policy-form-by-app-or-label',
          on: 'bottom',
        },
        canClickTarget: true,
        buttons: selectByNameAndProceedButtons,
      });

      // trigger click on Virtual Machines to toggle
      this.tour.addStep({
        id: 'step-policy-form-vm-select',
        text: `<p>When selecting by Virtual Machines, add namespaces that contain VMs, which can then be updated for greater specificity.</p>`,
        attachTo: {
          element: '#policy-by-namespace-or-vms',
          on: 'bottom',
        },
        buttons: [backButton, vmButton],
      });

      this.tour.addStep({
        id: 'step-policy-form-resources',
        text: `<p>By default all the resources in the application will be included in the backup.
        But perhaps there are resources you want to exclude, such as secrets or specific PVCs.</p>
        <p>If so, you would choose <i>Filter Resources</i> here.</p>`,
        attachTo: {
          element: '#policy-form-group-resources',
          on: 'bottom',
        },
        buttons: defaultBackAndNextButtons,
        beforeShowPromise: async () => {
          await this.ensureOptionSelected(
            '#policy-by-namespace-or-vms [data-testid="policy-form-policy-by-namespace-or-vms-btn-group-radio-option-Virtual Machines"]',
          );
        },
      });

      this.tour.addStep({
        id: 'step-policy-form-submit',
        text: `At this point you would create your policy and watch it set to work. But we won't submit this one.`,
        attachTo: {
          element: '#policyFormSubmitButton',
          on: 'top',
        },
        buttons: defaultBackAndNextButtons,
      });

      this.tour.addStep({
        id: 'step-dashboard-final',
        title: `Thanks!`,
        text: `<p>Hopefully this helped get you started.</p>
      <p>You can re-run this tour at anytime from Settings.</p>`,
        buttons: [backButton, completeTourButton],
      });
    },
  },
  mounted() {
    this.configureTour();
    // Global event listener allows other parts of UI to trigger start
    EventBus.$on('start-guided-tour', () => {
      this.startTour();
    });

    if (!get(this.cookieName)) {
      // Mark the tour dismissed right away.  User will either dismiss it or start it.
      set(this.cookieName, true);

      this.welcomeDialogTimeout = setTimeout(this.showTourWelcomeDialog, 3000);
    }
  },
  unmounted() {
    if (this.welcomeDialogTimeout) clearTimeout(this.welcomeDialogTimeout);
    EventBus.$off('start-guided-tour');
  },
};
</script>
